import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, ArrowRight, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fmtUSD } from "@/lib/api";

export default function CartDrawer() {
  const { items, subtotal, shipping, total, drawerOpen, setDrawerOpen, removeItem, updateQty } = useCart();

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50"
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-drawer-scrim"
          />
          <motion.aside
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 flex flex-col shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <ShoppingBag size={18} className="text-[#173a7a]" />
                <p className="font-display font-extrabold text-lg text-[#173a7a]">Your Cart</p>
              </div>
              <button
                data-testid="cart-drawer-close"
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-full hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="cart-empty">
                <div className="w-20 h-20 rounded-full bg-sky-50 flex items-center justify-center mb-4">
                  <ShoppingBag size={28} className="text-[#173a7a]" />
                </div>
                <p className="font-display text-xl font-bold text-slate-900">Your cart is empty</p>
                <p className="text-sm text-slate-500 mt-2 mb-6">Add a little softness to your day.</p>
                <Link
                  to="/shop"
                  onClick={() => setDrawerOpen(false)}
                  className="px-6 py-3 rounded-full bg-[#173a7a] text-white text-sm font-semibold hover:bg-[#1a4694]"
                  data-testid="cart-empty-shop-btn"
                >
                  Shop Products
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="cart-items-list">
                  {items.map((item) => (
                    <div key={item.slug} className="flex gap-4 pb-4 border-b border-slate-100 last:border-0" data-testid={`cart-item-${item.slug}`}>
                      <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl bg-sky-50 object-contain p-2" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 leading-snug">{item.name}</p>
                        <p className="text-sm text-slate-500 mt-1">{fmtUSD(item.price)}</p>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center rounded-full border border-slate-200">
                            <button
                              data-testid={`cart-qty-decrease-${item.slug}`}
                              onClick={() => updateQty(item.slug, item.quantity - 1)}
                              className="p-1.5 hover:text-[#173a7a]"
                            >
                              <Minus size={14} />
                            </button>
                            <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
                            <button
                              data-testid={`cart-qty-increase-${item.slug}`}
                              onClick={() => updateQty(item.slug, item.quantity + 1)}
                              className="p-1.5 hover:text-[#173a7a]"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                          <button
                            data-testid={`cart-remove-${item.slug}`}
                            onClick={() => removeItem(item.slug)}
                            className="text-slate-400 hover:text-red-500 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-100 px-6 py-5 space-y-3 bg-slate-50">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal">{fmtUSD(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Shipping {subtotal >= 25 && <span className="text-emerald-600 text-xs">FREE</span>}</span>
                    <span data-testid="cart-shipping">{shipping === 0 ? "Free" : fmtUSD(shipping)}</span>
                  </div>
                  <div className="flex justify-between font-display font-bold text-lg text-[#173a7a] pt-2 border-t border-slate-200">
                    <span>Total</span>
                    <span data-testid="cart-total">{fmtUSD(total)}</span>
                  </div>
                  <Link
                    to="/cart"
                    onClick={() => setDrawerOpen(false)}
                    data-testid="cart-drawer-checkout-btn"
                    className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-[#173a7a] text-white font-semibold text-sm hover:bg-[#1a4694]"
                  >
                    Review & Checkout <ArrowRight size={16} />
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
